import { useCallback } from 'react';
import {
  partialMatchKey,
  useMutation,
  useQueryClient,
  type MutateOptions,
  type QueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { MAX_BULK_MESSAGES, MAX_TRANSFER_MESSAGES } from '@/api/contract';
import { ClientErrorCodes } from '@/api/errors';
import { ApiError } from '@/api/http';
import { useApi } from '@/api/provider';
import { queryKeys } from '@/api/queryKeys';
import type {
  BulkResult,
  FlagUpdate,
  Folder,
  MessageDetail,
  MessageSummary,
  TransferItemResult,
  TransferMode,
} from '@/api/types';
import {
  applyCachePatches,
  cancelAffectedQueries,
  invalidateAffectedQueries,
  restoreSnapshot,
  snapshotAffectedQueries,
  type CachePatch,
  type CacheSnapshot,
} from '@/cache/patches';
import {
  adjustUnreadCount,
  countDeltasForFlagUpdate,
  countDeltasForRemoval,
  recordSourceFolder,
  removeMessageFromList,
  type FolderCountDeltas,
  type MessagePageCache,
} from '@/cache/messagePatches';
import {
  isConversationPageCache,
  reinsertConversations,
  removeMessagesFromConversations,
  type ConversationPageCache,
} from '@/cache/conversationPatches';
import { signalConversations } from '@/realtime/conversationSignals';
import {
  cachedMailboxEmail,
  failedAccountOf,
  mailboxFailureSentence,
  refreshAfterMailboxFailure,
  tagFailedAccount,
} from '@/features/dav/mailboxFailure';

/**
 * The optimistic-mutation kit (docs/plan/06-frontend-v1.md §1, WP-11).
 *
 * Every mail action — flag, mark read, archive, move, delete — has the same
 * shape and touches the same three caches (the folder list pages, the open
 * message detail, the folder tree's counters), so they share one hook. The
 * *cache* logic lives in `src/cache/`; this file only sequences it:
 *
 *   onMutate   cancel in-flight fetches → snapshot → apply patches
 *   onError    restore the snapshot verbatim
 *   onSuccess  a *partial* failure is still a failure: roll back too
 *   onSettled  mark stale with `refetchType: 'none'`
 *
 * The partial case is the one that is easy to get wrong. A bulk mail mutation
 * answers `200` with `{applied, failed}` even when every item failed (doc 10
 * §6.4: partial success is the normal case over IMAP), so a plain
 * `onSuccess` would leave the optimistic patch painted over rows the server
 * refused. `failed.length > 0` therefore restores the snapshot and refetches —
 * the simplest correct version of docs/plan/11 §9.4.
 *
 * Every request here is account-scoped (`/accounts/{id}/messages/…`, ADR 0012)
 * and every patch carries the same `accountId`, so the request and the cache
 * it repaints can never disagree about whose mail this is.
 *
 * Archive, delete and move are also *undoable* (ADR 0048, plan 15 §6.1): their
 * `mutate` patches the cache now and sends the request five seconds later,
 * unless Undo is pressed first. See "The undo window" at the end of the file.
 */

/** Context carried from `onMutate` to `onError`/`onSettled`. */
export interface OptimisticContext {
  snapshot: CacheSnapshot;
  patches: CachePatch[];
  /**
   * Puts back what the patches took away, and nothing else. Present only on a
   * deferred (undoable) mutation, whose snapshot can be five seconds old by the
   * time it is needed — restoring *that* verbatim would also revert every
   * other change made to the same lists in between.
   */
  reinstate?: () => void;
}

/**
 * True for a `BulkResult` in which the server refused at least one id.
 *
 * Structural rather than `instanceof`: the value arrives as JSON, and a
 * mutation whose response is not a bulk result at all (`send`) must simply not
 * match.
 */
export function bulkFailures(data: unknown): BulkResult['failed'] {
  if (typeof data !== 'object' || data === null) return [];
  const failed = (data as Partial<BulkResult>).failed;
  return Array.isArray(failed) ? failed : [];
}

/** The line to announce when a bulk mutation came back partly refused. */
export function bulkFailureMessage(data: unknown): string | undefined {
  const [first] = bulkFailures(data);
  if (!first) return undefined;
  return first.message || 'That change could not be saved and has been undone.';
}

export interface OptimisticMutationOptions<TVariables> {
  /** Runs the real request. Mutations take no `AbortSignal`: they must not be cancelled mid-flight. */
  mutationFn: (variables: TVariables) => Promise<unknown>;
  /** Describes the change as cache patches — the same values a WS event would emit. */
  patches: (variables: TVariables) => CachePatch[];
  /**
   * Serialization scope. Mutations sharing a scope id run strictly one at a
   * time, so an archive and a move issued a keystroke apart cannot interleave
   * and leave the cache disagreeing with the server.
   */
  scopeId: string;
  onSuccess?: (variables: TVariables) => void;
  /**
   * Makes `mutate` a deferred commit behind an Undo snackbar, and says what
   * the snackbar reads for these variables ("Message archived"). `mutateAsync`
   * is never deferred: a caller awaiting the server wants the server.
   */
  undoLabel?: (variables: TVariables) => string;
  /**
   * What the snackbar says when the request comes back a failure, in place of
   * the sentence that claimed it had worked.
   *
   * There has to be one. An optimistic mutation paints the result before the
   * server has seen it, and a deferred commit says so out loud five seconds
   * before the request even goes out — so a refusal that only rolled the cache
   * back left the user with "Message archived" as the last thing they were
   * told and a row that quietly reappeared. `error` is passed because the
   * useful sentence is often the server's reason, not the verb.
   */
  failureLabel?: (variables: TVariables, error: Error) => string;
}

export function useOptimisticMessageMutation<TVariables>(
  options: OptimisticMutationOptions<TVariables>,
): UseMutationResult<unknown, Error, TVariables, OptimisticContext> {
  const queryClient = useQueryClient();

  const mutation = useMutation<unknown, Error, TVariables, OptimisticContext>({
    scope: { id: options.scopeId },
    mutationFn: options.mutationFn,

    onMutate: async (variables) => {
      // A deferred commit patched the cache when it was asked for; running the
      // patches again now would count the folder deltas twice.
      const prepared = takePreparedContext(variables);
      if (prepared) return prepared;
      const patches = options.patches(variables);
      await cancelAffectedQueries(queryClient, patches);
      const snapshot = snapshotAffectedQueries(queryClient, patches);
      applyCachePatches(queryClient, patches);
      return { snapshot, patches };
    },

    onError: (error, variables, context) => {
      // Verbatim restore, not a re-fetch: the user is looking at the list.
      if (context) rollBack(queryClient, context);
      // And said out loud. The rollback alone is a list that changes back
      // under the user with no explanation for either move.
      // A connected mailbox whose saved password was rejected is not a fact
      // about these messages: name the mailbox, whatever the verb (ADR 0072).
      const aboutTheMailbox = mailboxFailureSentence(
        error,
        cachedMailboxEmail(queryClient, failedAccountOf(error)),
        'nothing was changed there',
      );
      refreshAfterMailboxFailure(queryClient, error);
      reportMutationFailure(
        aboutTheMailbox ?? options.failureLabel?.(variables, error) ?? GENERIC_FAILURE,
      );
    },

    onSuccess: (data, variables, context) => {
      // `200 {applied, failed}` with a non-empty `failed` is a partial refusal,
      // not a success: those ids never moved on the server. Restore the whole
      // snapshot rather than un-patching id by id — `onSettled` invalidates
      // straight after, so the rows the server *did* apply come back from the
      // refetch instead of from a half-reverted cache.
      if (context && bulkFailures(data).length > 0) {
        rollBack(queryClient, context);
        return;
      }
      options.onSuccess?.(variables);
    },

    onSettled: async (_data, _error, _variables, context) => {
      // Marks the affected keys stale without refetching under the user's
      // cursor. After a partial refusal the restored snapshot is the last
      // thing the server confirmed, so stale-not-refetched is the honest
      // state until something asks for the folder again.
      if (context) await invalidateAffectedQueries(queryClient, context.patches);
    },
  });

  const { mutate, mutateAsync } = mutation;
  const { undoLabel, patches: describe } = options;

  const mutateLater = useCallback(
    (
      variables: TVariables,
      mutateOptions?: MutateOptions<unknown, Error, TVariables, OptimisticContext>,
    ) => {
      // Nothing on screen can offer Undo (no snackbar is mounted — a popup
      // window, a test, a view without one), so holding the change back would
      // only be a hidden delay. Commit now, exactly as before chunk F.
      if (!undoLabel || !undoHostMounted()) {
        mutate(variables, mutateOptions ?? {});
        return;
      }
      void deferCommit(queryClient, {
        patches: describe(variables),
        label: undoLabel(variables),
        commit: (context) => {
          preparedContexts.set(variables as object, context);
          return mutateAsync(variables, mutateOptions ?? {});
        },
      });
    },
    [describe, mutate, mutateAsync, queryClient, undoLabel],
  );

  // The cast only widens the parameter list to TanStack's `void`-variables
  // overload; every undoable mutation here takes an object.
  return undoLabel ? { ...mutation, mutate: mutateLater as typeof mutate } : mutation;
}

/** The rollback half: surgical for a deferred commit, verbatim otherwise. */
function rollBack(client: QueryClient, context: OptimisticContext): void {
  if (context.reinstate) {
    context.reinstate();
    return;
  }
  restoreSnapshot(client, context.snapshot);
  // A conversation list (ADR 0077) put back verbatim has also lost whatever
  // its `sync` applied since the snapshot — and got the token from before
  // those changes back with it. So each such list syncs again from that
  // token: the server re-reports exactly what the restore took away.
  const accounts = new Set<string>();
  for (const [queryKey, data] of context.snapshot) {
    if (!isConversationPageCache(data)) continue;
    if (partialMatchKey(queryKey, queryKeys.unifiedConversationsScope())) {
      for (const patch of context.patches) accounts.add(patch.accountId);
    } else if (typeof queryKey[1] === 'string') {
      accounts.add(queryKey[1]);
    }
  }
  for (const accountId of accounts) signalConversations(client, { accountId });
}

/**
 * Contexts a deferred commit prepared at patch time, handed to `onMutate` when
 * the real mutation finally runs. Keyed by the variables object, which TanStack
 * passes through to `onMutate` by reference.
 */
const preparedContexts = new WeakMap<object, OptimisticContext>();

function takePreparedContext(variables: unknown): OptimisticContext | undefined {
  if (typeof variables !== 'object' || variables === null) return undefined;
  const context = preparedContexts.get(variables);
  preparedContexts.delete(variables);
  return context;
}

/* ------------------------------------------------------------ the actions */

/** Scope id: serialize every message mutation for one account. */
function messageScopeId(accountId: string): string {
  return `messages:${accountId}`;
}

/* ------------------------------------------------- routing by the row (ADR 0056) */

/**
 * A role folder, per account.
 *
 * Archive and Trash are per mailbox, so the unified view cannot hand these
 * hooks one id: `e` on a row must move it into *that row's* Archive. A plain
 * string still works and means "this id, whatever the account" — which is what
 * a single-account list has always meant.
 */
export type RoleFolder = string | undefined | ((accountId: string) => string | undefined);

function roleFolderFor(role: RoleFolder, accountId: string): string | undefined {
  return typeof role === 'function' ? role(accountId) : role;
}

/**
 * Splits a batch by the account each row belongs to, preserving order.
 *
 * `MessageSummary.accountId` is the truth here, not the account the view was
 * built with: it is correct in the single-account case by definition and it is
 * the only thing that is correct in the merged one. An empty batch falls back
 * to the hook's own account so a mutation with no rows still addresses
 * something rather than sending `/accounts//…`.
 */
export function groupByAccount<T extends MessageSummary>(
  messages: readonly T[],
  fallbackAccountId: string,
): [string, T[]][] {
  const groups = new Map<string, T[]>();
  for (const message of messages) {
    const accountId = message.accountId || fallbackAccountId;
    const group = groups.get(accountId);
    if (group) group.push(message);
    else groups.set(accountId, [message]);
  }
  if (groups.size === 0) return [];
  return [...groups.entries()];
}

/**
 * Runs the requests per account and folds the answers into one `BulkResult`.
 *
 * One account's rows go out in chunks of at most `MAX_BULK_MESSAGES` — the
 * server refuses a bigger request outright, and one conversation alone can
 * hold more (ADR 0077). Chunks of one account run in turn (they share the
 * account's IMAP lease anyway); accounts run side by side. It is still one
 * mutation: one optimistic patch, one Undo, one result.
 *
 * A rejection of an account's FIRST chunk fails the whole mutation, which is
 * the honest outcome: nothing of that account moved, and the optimistic patch
 * covered every row. A later chunk's rejection cannot say that — the chunks
 * before it did reach the server — so its ids are reported as `failed`
 * instead, and the shared partial-failure path takes it from there.
 */
async function fanOut(
  groups: readonly [string, MessageSummary[]][],
  run: (accountId: string, messages: MessageSummary[]) => Promise<unknown>,
): Promise<BulkResult> {
  const answers = await Promise.all(
    groups.map(async ([accountId, rows]) => {
      const results: BulkResult[] = [];
      for (let start = 0; start < rows.length; start += MAX_BULK_MESSAGES) {
        const chunk = rows.slice(start, start + MAX_BULK_MESSAGES);
        try {
          results.push(asBulkResult(await run(accountId, chunk)));
        } catch (error) {
          tagFailedAccount(error, accountId);
          if (start === 0) throw error;
          results.push({
            applied: [],
            failed: chunk.map((message) => ({
              id: message.id,
              code: error instanceof ApiError ? error.code : ClientErrorCodes.Unknown,
              message: error instanceof Error ? error.message : 'The operation failed.',
            })),
          });
        }
      }
      return results;
    }),
  );
  const merged: BulkResult = { applied: [], failed: [] };
  for (const result of answers.flat()) {
    merged.applied.push(...result.applied);
    merged.failed.push(...result.failed);
  }
  return merged;
}

function asBulkResult(answer: unknown): BulkResult {
  const result = (answer ?? {}) as Partial<BulkResult>;
  return {
    applied: Array.isArray(result.applied) ? result.applied : [],
    failed: Array.isArray(result.failed) ? result.failed : [],
  };
}

/**
 * What every verb's variables may say besides the messages: how many
 * CONVERSATIONS they are (ADR 0077). A verb on three conversation rows is a
 * verb on all their members — one request, one patch — but it is three things
 * to the person who pressed it, and the Undo says so.
 */
export interface ConversationCount {
  conversations?: number;
}

export interface FlagVariables extends ConversationCount {
  /** The rows as currently cached — needed to compute honest unread deltas. */
  messages: MessageSummary[];
  update: FlagUpdate;
}

/** Add/remove flags (star, mark read, mark unread) with rollback. */
export function useSetFlagsMutation(accountId: string | undefined) {
  const api = useApi();
  const id = accountId ?? '';
  return useOptimisticMessageMutation<FlagVariables>({
    scopeId: messageScopeId(id),
    mutationFn: ({ messages, update }) =>
      fanOut(groupByAccount(messages, id), (account, rows) =>
        api.messages.updateFlags(
          account,
          rows.map((message) => message.id),
          update,
        ),
      ),
    patches: ({ messages, update }) =>
      groupByAccount(messages, id).flatMap(([account, rows]) => {
        const origins = recordSourceFolder(rows);
        return [
          {
            kind: 'message-flags' as const,
            accountId: account,
            messageIds: rows.map((message) => message.id),
            update,
          },
          {
            kind: 'folder-counts' as const,
            accountId: account,
            deltas: countDeltasForFlagUpdate(origins, update),
          },
        ];
      }),
  });
}

export interface MoveVariables extends ConversationCount {
  messages: MessageSummary[];
  targetFolderId: string;
}

/** Archive, delete, spam, not spam. */
export interface MessagesVariables extends ConversationCount {
  messages: MessageSummary[];
}

/**
 * Move to another folder. `archive` is this with the archive folder as target.
 *
 * A folder id names a mailbox inside ONE account (ADR 0012), so this verb is
 * the one that cannot fan out: a target from account A means nothing in
 * account B. Rows from another mailbox than the target's go through
 * `useTransferMessagesMutation` instead (ADR 0108; `moveOrTransfer` in
 * MailView splits them), and the grouping below exists only so a
 * single-account batch keeps working when it arrives through the unified list.
 */
export function useMoveMessagesMutation(accountId: string | undefined) {
  const api = useApi();
  const id = accountId ?? '';
  return useOptimisticMessageMutation<MoveVariables>({
    scopeId: messageScopeId(id),
    mutationFn: ({ messages, targetFolderId }) =>
      fanOut(groupByAccount(messages, id), (account, rows) =>
        api.messages.move(account, {
          messageIds: rows.map((message) => message.id),
          targetFolderId,
        }),
      ),
    patches: ({ messages, targetFolderId }) =>
      groupByAccount(messages, id).flatMap(([account, rows]) =>
        movePatches(account, rows, targetFolderId),
      ),
    undoLabel: ({ messages, conversations }) => countLabel(messages.length, 'moved', undefined, conversations),
  });
}

/**
 * Archive (keyboard `e`).
 *
 * The backend has a dedicated endpoint, but the cache effect is a move, so it
 * emits move patches. `archiveFolderId` is only needed for the destination
 * folder's counters; without it the source folder is still patched correctly.
 */
export function useArchiveMessagesMutation(
  accountId: string | undefined,
  archiveFolderId: RoleFolder,
) {
  const api = useApi();
  const id = accountId ?? '';
  return useOptimisticMessageMutation<MessagesVariables>({
    scopeId: messageScopeId(id),
    mutationFn: ({ messages }) =>
      fanOut(groupByAccount(messages, id), (account, rows) =>
        api.messages.archive(
          account,
          rows.map((message) => message.id),
        ),
      ),
    patches: ({ messages }) =>
      groupByAccount(messages, id).flatMap(([account, rows]) =>
        movePatches(account, rows, roleFolderFor(archiveFolderId, account)),
      ),
    undoLabel: ({ messages, conversations }) =>
      countLabel(messages.length, 'archived', undefined, conversations),
    // The one refusal worth naming: the backend answers 404 `no_archive_folder`
    // when the mailbox has no Archive to move into. The affordance is not
    // offered on such a mailbox (`MailView`), so this is the floor under a
    // folder that went away between the folder list and the request — but it
    // is the sentence that has to be said, because "Message archived" was.
    failureLabel: ({ messages }, error) =>
      noRoleFolder(error, 'no_archive_folder')
        ? missingRoleFolder(error, 'Archive', 'nothing was archived')
        : messages.length === 1
          ? 'That message could not be archived, and is back where it was.'
          : 'Those messages could not be archived, and are back where they were.',
  });
}

/**
 * Report spam (keyboard `!`): `$Junk` on, `$NotJunk` off, into the row's own
 * mailbox's Junk folder.
 *
 * Archive's shape exactly — a move, one request per account, one Undo for the
 * batch — because to the list that is all it is. The flags are the server's
 * business: they are what teaches the host's filter, and nothing on screen
 * reads them back.
 */
export function useReportSpamMutation(accountId: string | undefined, junkFolderId: RoleFolder) {
  const api = useApi();
  const id = accountId ?? '';
  return useOptimisticMessageMutation<MessagesVariables>({
    scopeId: messageScopeId(id),
    mutationFn: ({ messages }) =>
      fanOut(groupByAccount(messages, id), (account, rows) =>
        api.messages.reportSpam(
          account,
          rows.map((message) => message.id),
        ),
      ),
    patches: ({ messages }) =>
      groupByAccount(messages, id).flatMap(([account, rows]) =>
        movePatches(account, rows, roleFolderFor(junkFolderId, account)),
      ),
    undoLabel: ({ messages, conversations }) =>
      countLabel(messages.length, 'reported as spam', 'Reported as spam', conversations),
    // As with Archive, the verb is withheld on a mailbox with no Junk folder;
    // this is the floor under one that went away after the tree was read.
    failureLabel: ({ messages }, error) =>
      noRoleFolder(error, 'no_junk_folder')
        ? missingRoleFolder(error, 'Junk', 'nothing was reported as spam')
        : messages.length === 1
          ? 'That message could not be reported as spam, and is back where it was.'
          : 'Those messages could not be reported as spam, and are back where they were.',
  });
}

/**
 * Not spam: `$NotJunk` on, `$Junk` off, back to the row's own Inbox. The same
 * verb as Report spam, pointed the other way.
 */
export function useNotSpamMutation(accountId: string | undefined, inboxFolderId: RoleFolder) {
  const api = useApi();
  const id = accountId ?? '';
  return useOptimisticMessageMutation<MessagesVariables>({
    scopeId: messageScopeId(id),
    mutationFn: ({ messages }) =>
      fanOut(groupByAccount(messages, id), (account, rows) =>
        api.messages.notSpam(
          account,
          rows.map((message) => message.id),
        ),
      ),
    patches: ({ messages }) =>
      groupByAccount(messages, id).flatMap(([account, rows]) =>
        movePatches(account, rows, roleFolderFor(inboxFolderId, account)),
      ),
    undoLabel: ({ messages, conversations }) =>
      countLabel(messages.length, 'marked as not spam', 'Marked as not spam', conversations),
    failureLabel: ({ messages }) =>
      messages.length === 1
        ? 'That message could not be marked as not spam, and is back in Junk.'
        : 'Those messages could not be marked as not spam, and are back in Junk.',
  });
}

/** A 404 that names a missing role folder rather than a missing message. */
function noRoleFolder(error: unknown, reason: string): boolean {
  return error instanceof ApiError && error.status === 404 && error.details?.reason === reason;
}

/**
 * The sentence for a role folder the mailbox does not have (#68).
 *
 * Two different situations reach the same 404, and they need different
 * advice: a mailbox that never had the folder (choose one), and one where the
 * person chose a folder in Settings that was later renamed or deleted in
 * another app (`details.stale_override`), where "has no Archive folder" would
 * read as the app having forgotten their choice. Either way the fix is the
 * Default folders card, so the sentence names where it is.
 */
function missingRoleFolder(error: unknown, folder: string, outcome: string): string {
  const stale = error instanceof ApiError && error.details?.stale_override === true;
  return stale
    ? `Your chosen ${folder} folder no longer exists, so ${outcome}. Choose another in Settings → Accounts.`
    : `This mailbox has no ${folder} folder, so ${outcome}. Choose one in Settings → Accounts.`;
}

/**
 * Delete (keyboard `#`): move to Trash, or expunge when already there.
 *
 * Either way the row is gone from the list it was in, and the cached detail is
 * dropped rather than left pointing at a message that may no longer exist.
 */
export function useDeleteMessagesMutation(
  accountId: string | undefined,
  trashFolderId: RoleFolder,
) {
  const api = useApi();
  const id = accountId ?? '';
  return useOptimisticMessageMutation<MessagesVariables>({
    scopeId: messageScopeId(id),
    mutationFn: ({ messages }) =>
      fanOut(groupByAccount(messages, id), (account, rows) =>
        api.messages.remove(
          account,
          rows.map((message) => message.id),
        ),
      ),
    patches: ({ messages }) =>
      groupByAccount(messages, id).flatMap(([account, rows]) => {
        const trash = roleFolderFor(trashFolderId, account);
        const origins = recordSourceFolder(rows);
        const target = allIn(rows, trash) ? undefined : trash;
        return [
          {
            kind: 'messages-removed' as const,
            accountId: account,
            messageIds: rows.map((message) => message.id),
          },
          {
            kind: 'folder-counts' as const,
            accountId: account,
            deltas: countDeltasForRemoval(origins, target),
          },
        ];
      }),
    // The backend answers 404 `no_trash_folder` when a mailbox has nowhere to
    // put deleted mail; "Message deleted" was the last thing said (#343).
    failureLabel: ({ messages }, error) =>
      noRoleFolder(error, 'no_trash_folder')
        ? missingRoleFolder(error, 'Trash', 'nothing was deleted')
        : messages.length === 1
          ? 'That message could not be deleted, and is back where it was.'
          : 'Those messages could not be deleted, and are back where they were.',
    // From Trash this is an expunge — the one action here that nothing but
    // Undo can take back — so the sentence says so. Across accounts it only
    // says "deleted forever" when it is true of every row.
    undoLabel: ({ messages, conversations }) =>
      countLabel(
        messages.length,
        groupByAccount(messages, id).every(([account, rows]) =>
          allIn(rows, roleFolderFor(trashFolderId, account)),
        ) && messages.length > 0
          ? 'deleted forever'
          : 'deleted',
        undefined,
        conversations,
      ),
  });
}

function allIn(messages: readonly MessageSummary[], folderId: string | undefined): boolean {
  return folderId !== undefined && messages.every((message) => message.folderId === folderId);
}

/**
 * "Message archived", "3 messages archived". `single` replaces the one-message
 * sentence where "Message reported as spam" would read worse than the verb
 * alone.
 */
function countLabel(count: number, verb: string, single?: string, conversations?: number): string {
  // Counted as the rows the person acted on: "3 conversations archived",
  // however many messages were in them.
  if (conversations !== undefined) {
    return conversations === 1 ? `Conversation ${verb}` : `${conversations} conversations ${verb}`;
  }
  if (count === 1) return single ?? `Message ${verb}`;
  return `${count} messages ${verb}`;
}

function movePatches(
  accountId: string,
  messages: MessageSummary[],
  targetFolderId: string | undefined,
): CachePatch[] {
  const ids = messages.map((message) => message.id);
  const origins = recordSourceFolder(messages);
  const patches: CachePatch[] = [
    {
      kind: 'folder-counts',
      accountId,
      deltas: countDeltasForRemoval(origins, targetFolderId),
    },
  ];
  patches.unshift(
    targetFolderId
      ? { kind: 'messages-moved', accountId, messageIds: ids, targetFolderId }
      : { kind: 'messages-removed', accountId, messageIds: ids },
  );
  return patches;
}

/* ------------------------------------------- moving between mailboxes (ADR 0108) */

export interface TransferVariables {
  /** Rows of any mailbox but the target's; a unified selection may span several. */
  messages: MessageSummary[];
  targetAccountId: string;
  targetFolderId: string;
  /** How the notice names the target: "Archive in alex@studio-nine.org". */
  targetLabel: string;
  mode: TransferMode;
}

/** What one transfer did, counted for the notice. */
export interface TransferOutcome {
  moved: number;
  copied: number;
  /** Copied, but the source could not remove only these (no UIDPLUS). */
  notRemoved: number;
  failed: number;
  /**
   * Sent, but the answer was lost in transport (a dropped connection, an
   * edge's own 502/504): the server may have moved some, all or none of
   * them, so the notice claims neither.
   */
  unknown: number;
  /** The first per-message reason, for the notice. */
  firstError?: string | undefined;
}

function plural(count: number): string {
  return count === 1 ? '1 message' : `${count} messages`;
}

/**
 * The sentence a finished transfer leaves in the snackbar. No Undo (ADR 0108):
 * a transfer cannot be taken back without a second one, and it is safe by
 * construction, so it says what happened instead.
 */
export function transferNotice(outcome: TransferOutcome, mode: TransferMode, targetLabel: string): string {
  const parts: string[] = [];
  if (outcome.unknown > 0) parts.push(`${plural(outcome.unknown)}: result unknown; the lists will refresh.`);
  const done = mode === 'move' ? outcome.moved : outcome.copied;
  if (done > 0) parts.push(`${plural(done)} ${mode === 'move' ? 'moved' : 'copied'} to ${targetLabel}.`);
  if (outcome.notRemoved > 0) {
    parts.push(
      `${plural(outcome.notRemoved)} copied to ${targetLabel} but still in the original mailbox, marked deleted: its server cannot remove only those.`,
    );
  }
  if (outcome.failed > 0) {
    const verb = mode === 'move' ? 'moved' : 'copied';
    parts.push(
      `${plural(outcome.failed)} could not be ${verb}${outcome.firstError ? `: ${outcome.firstError}` : '.'}`,
    );
  }
  return parts.join(' ') || 'Nothing was changed.';
}

/**
 * A transfer request that failed without an answer from the server itself:
 * fetch rejected or the body was cut off (status 0, or not an ApiError at
 * all), or an edge answered with something other than our error envelope
 * (`request_failed`, typically a proxy's 502/504). Unlike a refusal, it says
 * nothing about whether the server did the work.
 */
function isTransportFailure(error: unknown): boolean {
  if (!(error instanceof ApiError)) return !(error instanceof DOMException && error.name === 'AbortError');
  return error.status === 0 || error.code === ClientErrorCodes.Network || error.code === ClientErrorCodes.RequestFailed;
}

/**
 * Move or copy rows into a folder of ANOTHER mailbox (ADR 0108).
 *
 * Not the optimistic kit, on purpose. Each message is downloaded from one
 * server and uploaded to another, so a batch takes seconds, a partial batch is
 * normal, and there is no Undo. Painting the rows away first and then putting
 * back the ones that failed would show a list that is wrong for the whole
 * length of the transfer; instead a row leaves the source list when the server
 * says it left the source mailbox, chunk by chunk, and a failed row simply
 * stays where it is.
 *
 * Per source account, in chunks of `MAX_TRANSFER_MESSAGES`, strictly in turn:
 * the server holds two leased connections per request, and the progress
 * notice counts up as the chunks come back.
 */
export function useTransferMessagesMutation() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<TransferOutcome, Error, TransferVariables>({
    scope: { id: 'messages:transfer' },
    mutationFn: async ({ messages, targetAccountId, targetFolderId, targetLabel, mode }) => {
      const outcome: TransferOutcome = { moved: 0, copied: 0, notRemoved: 0, failed: 0, unknown: 0 };
      const groups = groupByAccount(messages, '').filter(([account]) => account !== targetAccountId);
      const total = groups.reduce((sum, [, rows]) => sum + rows.length, 0);
      const verbing = mode === 'move' ? 'Moving' : 'Copying';
      let sent = 0;
      for (const [sourceAccountId, rows] of groups) {
        for (let start = 0; start < rows.length; start += MAX_TRANSFER_MESSAGES) {
          const chunk = rows.slice(start, start + MAX_TRANSFER_MESSAGES);
          showMailNotice(
            total > chunk.length
              ? `${verbing} ${sent + 1}–${sent + chunk.length} of ${total} messages to ${targetLabel}…`
              : `${verbing} ${plural(chunk.length)} to ${targetLabel}…`,
          );
          let results: TransferItemResult[];
          try {
            ({ results } = await api.messages.transfer(sourceAccountId, {
              messageIds: chunk.map((row) => row.id),
              targetAccountId,
              targetFolderId,
              mode,
            }));
          } catch (error) {
            if (isTransportFailure(error)) {
              // No answer in the server's own words: the request may have
              // been carried out, wholly or in part, before its answer was
              // lost (the server cuts a batch at its budget, but a proxy or
              // the network can still drop the reply). Claim nothing for this
              // chunk; onSettled refetches both mailboxes, which shows what
              // really happened. The later chunks are not sent.
              outcome.unknown += chunk.length;
              const notSent = rows.length - start - chunk.length;
              if (notSent > 0) {
                outcome.failed += notSent;
                outcome.firstError ??= 'the connection to the server failed, so they were not sent.';
              }
              sent += rows.length - start;
              break;
            }
            // The whole request was refused (a mailbox that wants its
            // password, a target that went away): nothing of this chunk was
            // touched, and the account's later chunks would be refused alike.
            tagFailedAccount(error, sourceAccountId);
            refreshAfterMailboxFailure(queryClient, error);
            outcome.failed += rows.length - start;
            outcome.firstError ??=
              mailboxFailureSentence(
                error,
                cachedMailboxEmail(queryClient, sourceAccountId),
                'nothing was moved from it',
              ) ?? (error instanceof Error && error.message !== '' ? error.message : undefined);
            sent += rows.length - start;
            break;
          }
          sent += chunk.length;
          const byId = new Map(chunk.map((row) => [row.id, row]));
          const gone: MessageSummary[] = [];
          const markedDeleted: string[] = [];
          for (const result of results) {
            if (result.status === 'failed') {
              outcome.failed += 1;
              outcome.firstError ??= result.error?.message;
              continue;
            }
            if (result.status === 'copied_not_removed') {
              outcome.notRemoved += 1;
              markedDeleted.push(result.id);
              continue;
            }
            if (mode === 'move') outcome.moved += 1;
            else outcome.copied += 1;
            // `already_there` on a move: the APPEND was skipped, the source
            // copy was still removed (ADR 0108 step 4).
            const row = byId.get(result.id);
            if (row && mode === 'move') gone.push(row);
          }
          // An id the server did not answer for is not known to have moved.
          outcome.failed += Math.max(0, chunk.length - results.length);
          const patches: CachePatch[] = [];
          if (gone.length > 0) patches.push(...movePatches(sourceAccountId, gone, undefined));
          if (markedDeleted.length > 0) {
            patches.push({
              kind: 'message-flags',
              accountId: sourceAccountId,
              messageIds: markedDeleted,
              update: { add: ['deleted'] },
            });
          }
          if (patches.length > 0) {
            await cancelAffectedQueries(queryClient, patches);
            applyCachePatches(queryClient, patches);
            await invalidateAffectedQueries(queryClient, patches);
          }
        }
      }
      return outcome;
    },
    onSuccess: (outcome, { mode, targetLabel }) => showMailNotice(transferNotice(outcome, mode, targetLabel)),
    // A failed request is handled per chunk above; an error that still lands
    // here may follow chunks the server carried out, so it claims nothing
    // either way, and onSettled refetches both mailboxes.
    onError: () => showMailNotice('Something went wrong while transferring those messages; the lists will refresh.'),
    onSettled: async (_data, _error, { messages, targetAccountId }) => {
      // The target gained mail this client holds no row for (its id is the
      // target server's to mint), and every source lost some: both mailboxes'
      // lists and counters are refetched. The live events say the same soon.
      const accounts = new Set([targetAccountId, ...messages.map((message) => message.accountId)]);
      await Promise.all([
        ...[...accounts].flatMap((account) => [
          queryClient.invalidateQueries({ queryKey: queryKeys.account(account).messagesScope() }),
          queryClient.invalidateQueries({ queryKey: queryKeys.account(account).conversationsScope() }),
          queryClient.invalidateQueries({ queryKey: queryKeys.account(account).folders() }),
        ]),
        queryClient.invalidateQueries({ queryKey: queryKeys.unifiedMessagesScope() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.unifiedConversationsScope() }),
      ]);
    },
  });
}

/* -------------------------------------------------------- the undo window */

/*
 * Undo is a *deferred commit*, not an inverse mutation (plan 15 §6.1).
 *
 * The alternative — send now, and have Undo send the reverse — makes Undo a
 * second IMAP change that can itself fail, needs the message's new id in the
 * folder it landed in, and cannot exist at all for a delete from Trash, which
 * is an expunge. Deferring keeps the server untouched until the window closes,
 * so Undo is purely local: stop the clock, put the rows back.
 *
 * The rules, each one a test in `useMessageMutations.undo.test.tsx`:
 *
 * - **One pending action at a time**, as in Gmail: a second action commits the
 *   first immediately and takes over the snackbar.
 * - **The row goes now, the request goes later.** The cache patch is applied
 *   at once; the mutation — and so its broadcast on the cross-window channel
 *   (`useCachePatchChannel`) — runs only at commit. Another tab therefore never
 *   sees a row vanish that Undo would bring back, and there is nothing to
 *   retract when Undo is pressed.
 * - **A refetch inside the window cannot resurrect the row.** The server still
 *   has it, so any list or folder fetch that lands before the commit settles
 *   has the pending patch re-applied to its result.
 * - **Undo is surgical.** It re-inserts the removed rows at their old positions
 *   and reverses the counter deltas instead of restoring the snapshot: five
 *   seconds is long enough for a star, a mark-read or a live event to have
 *   changed the same list, and a verbatim restore would silently revert them.
 * - **Leaving commits.** `visibilitychange → hidden` and `pagehide` commit the
 *   pending action at once. The user asked for it and did not undo it; the
 *   only way to undo it is the Undo control, which runs synchronously before
 *   either event can fire, so this can never commit an undone action. `hidden`
 *   is the event that matters — it fires on tab close, navigation, app switch
 *   and before a mobile browser discards the tab, while the page is still
 *   alive; `pagehide` is the backstop. What this cannot promise is that a
 *   request started as the document is torn down finishes: without `keepalive`
 *   on the transport a close can abort it, and then the row is simply still
 *   there next time — a visible, repeatable miss rather than a silent or
 *   destructive one.
 * - **No snackbar, no delay.** The window only exists while something on
 *   screen can offer Undo (`registerUndoHost`). With no host mounted a mutation
 *   commits at once, and when the last host unmounts (the user left Mail for
 *   Calendar) the pending action is committed rather than left running on a
 *   timer nobody can see.
 * - **Holding the snackbar stops the clock** (hover or focus, WCAG 2.2.1), and
 *   releasing it restarts with what was left, never less than two seconds.
 */

/** How long an undoable action waits before it reaches the server. */
export const UNDO_WINDOW_MS = 5000;

/** The least time left after the snackbar is released, so it cannot vanish on blur. */
const UNDO_RESUME_MIN_MS = 2000;

/** What the snackbar needs to know. A new object per pending action. */
export interface PendingUndo {
  id: number;
  label: string;
}

interface PendingEntry extends PendingUndo {
  commit: () => void;
  undo: () => void;
  timer: ReturnType<typeof setTimeout> | undefined;
  deadline: number;
  remaining: number;
  held: boolean;
}

let pending: PendingEntry | undefined;
let pendingView: PendingUndo | undefined;
let nextPendingId = 1;
let hostCount = 0;
const undoListeners = new Set<() => void>();

/* ------------------------------------------------- saying that it failed */

/**
 * The notice a rolled-back mutation leaves behind.
 *
 * Same store shape as the pending undo, and the same snackbar renders it —
 * with no action on it, because a rollback has already happened and there is
 * nothing left to press. It is a `status`, not an `alert`: nothing is on fire,
 * the list simply says what it now knows.
 */
export interface MutationFailure {
  id: number;
  label: string;
}

/** What is said when nothing more specific is known. */
const GENERIC_FAILURE = 'That change could not be saved, and has been undone.';

/** How long the notice stays before the snackbar closes itself. */
export const FAILURE_NOTICE_MS = 8_000;

let failure: MutationFailure | undefined;
let failureTimer: ReturnType<typeof setTimeout> | undefined;
const failureListeners = new Set<() => void>();

function emitFailure(): void {
  for (const listener of failureListeners) listener();
}

export function subscribeMutationFailure(listener: () => void): () => void {
  failureListeners.add(listener);
  return () => {
    failureListeners.delete(listener);
  };
}

export function getMutationFailure(): MutationFailure | undefined {
  return failure;
}

/** Escape on the notice, and the reset every test needs. */
export function dismissMutationFailure(): void {
  clearTimeout(failureTimer);
  failureTimer = undefined;
  if (failure === undefined) return;
  failure = undefined;
  emitFailure();
}

/**
 * Says why a verb did nothing, on the same snackbar a refused change uses
 * (#343): Archive on a mailbox with no Archive folder used to be silent.
 */
export function reportActionNotice(label: string): void {
  reportMutationFailure(label);
}

function reportMutationFailure(label: string): void {
  clearTimeout(failureTimer);
  failure = { id: nextPendingId++, label };
  failureTimer = setTimeout(dismissMutationFailure, FAILURE_NOTICE_MS);
  emitFailure();
}

/**
 * A plain sentence in the mail snackbar, with no action on it — "alice@… is
 * blocked". The same store and region as the failure notice: both are a thing
 * the list now knows and says once, and a second region would be a second
 * place to look. A pending Undo still outranks it.
 */
export function showMailNotice(label: string): void {
  reportMutationFailure(label);
}

function emitUndo(): void {
  pendingView = pending ? { id: pending.id, label: pending.label } : undefined;
  for (const listener of undoListeners) listener();
}

/** `useSyncExternalStore` pair for the snackbar. */
export function subscribeUndo(listener: () => void): () => void {
  undoListeners.add(listener);
  return () => {
    undoListeners.delete(listener);
  };
}

export function getPendingUndo(): PendingUndo | undefined {
  return pendingView;
}

/**
 * Declares that something on screen can offer Undo. Returns the release; the
 * last release commits whatever is pending.
 */
export function registerUndoHost(): () => void {
  hostCount += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    hostCount -= 1;
    if (hostCount === 0) commitPending();
  };
}

function undoHostMounted(): boolean {
  return hostCount > 0;
}

/** Commits the pending action now (a second action, leaving, dismissing). */
export function commitPending(): void {
  const entry = takePending();
  entry?.commit();
}

/** Cancels the pending action and restores its rows. False when there was none. */
export function undoPending(): boolean {
  const entry = takePending();
  if (!entry) return false;
  entry.undo();
  return true;
}

/** Stops (true) or restarts (false) the pending action's clock. */
export function holdPending(held: boolean): void {
  const entry = pending;
  if (!entry || entry.held === held) return;
  entry.held = held;
  if (held) {
    clearTimeout(entry.timer);
    entry.timer = undefined;
    entry.remaining = Math.max(0, entry.deadline - Date.now());
  } else {
    startClock(entry, Math.max(entry.remaining, UNDO_RESUME_MIN_MS));
  }
}

function startClock(entry: PendingEntry, ms: number): void {
  clearTimeout(entry.timer);
  entry.deadline = Date.now() + ms;
  entry.remaining = ms;
  entry.timer = setTimeout(commitPending, ms);
}

function takePending(): PendingEntry | undefined {
  const entry = pending;
  if (!entry) return undefined;
  clearTimeout(entry.timer);
  pending = undefined;
  setLifecycleListeners(false);
  emitUndo();
  return entry;
}

function onVisibilityChange(): void {
  if (document.visibilityState === 'hidden') commitPending();
}

function setLifecycleListeners(on: boolean): void {
  if (typeof window === 'undefined') return;
  if (on) {
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', commitPending);
  } else {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', commitPending);
  }
}

interface DeferredCommit {
  patches: CachePatch[];
  label: string;
  /** Runs the real mutation with the context prepared here. */
  commit: (context: OptimisticContext) => Promise<unknown>;
}

/**
 * The deferred half of an undoable `mutate`: the same cancel → snapshot →
 * patch sequence `onMutate` runs, done now, with the request parked behind the
 * undo window.
 */
async function deferCommit(client: QueryClient, deferred: DeferredCommit): Promise<void> {
  // Gmail's rule: the previous action is final once another one is taken.
  commitPending();
  // A notice about the last action must not sit under this one's Undo.
  dismissMutationFailure();

  const { patches } = deferred;
  // Awaited for the same reason as in `onMutate`: a cancelled fetch reverts
  // its query's state asynchronously, which would repaint the row after the
  // patch below had removed it.
  await cancelAffectedQueries(client, patches);
  const snapshot = snapshotAffectedQueries(client, patches);
  applyCachePatches(client, patches);
  const stopGuard = guardPendingPatches(client, patches);

  const context: OptimisticContext = {
    snapshot,
    patches,
    reinstate: () => reinstatePatches(client, snapshot, patches),
  };

  const entry: PendingEntry = {
    id: nextPendingId++,
    label: deferred.label,
    commit: () => {
      // The guard stays up until the server has answered: a list fetched
      // while the request is in flight can still predate it.
      deferred
        .commit(context)
        .catch(() => {
          // Surfaced through the mutation's own error state and rolled back
          // in its `onError`; nothing more to do here.
        })
        .finally(stopGuard);
    },
    undo: () => {
      stopGuard();
      context.reinstate?.();
      void invalidateAffectedQueries(client, patches);
    },
    timer: undefined,
    deadline: 0,
    remaining: UNDO_WINDOW_MS,
    held: false,
  };

  pending = entry;
  startClock(entry, UNDO_WINDOW_MS);
  setLifecycleListeners(true);
  emitUndo();
}

/** Ids a patch set takes out of the lists. */
function removedIds(patches: readonly CachePatch[]): Set<string> {
  const ids = new Set<string>();
  for (const patch of patches) {
    if (patch.kind === 'messages-moved' || patch.kind === 'messages-removed') {
      for (const id of patch.messageIds) ids.add(id);
    }
  }
  return ids;
}

/**
 * Re-applies a pending patch set to anything *fetched* while it is pending.
 *
 * Only fetch results (`success` and not `manual`): a manual write is either
 * this guard's own or another patch, which already started from the patched
 * cache.
 */
function guardPendingPatches(client: QueryClient, patches: readonly CachePatch[]): () => void {
  const ids = [...removedIds(patches)];
  const held: PendingRemoval[] = patches.flatMap((patch) =>
    patch.kind === 'messages-moved' || patch.kind === 'messages-removed'
      ? [{ accountId: patch.accountId, messageIds: patch.messageIds }]
      : [],
  );
  for (const entry of held) pendingRemovalSet.add(entry);
  const unsubscribe = client.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || event.action.type !== 'success' || event.action.manual) return;
    const { queryKey } = event.query;
    for (const patch of patches) {
      const keys = queryKeys.account(patch.accountId);
      if (patch.kind === 'folder-counts' && partialMatchKey(queryKey, keys.folders())) {
        client.setQueryData<Folder[]>(queryKey, (folders) => adjustUnreadCount(folders, patch.deltas));
      } else if (
        (patch.kind === 'messages-moved' || patch.kind === 'messages-removed') &&
        (partialMatchKey(queryKey, keys.conversationsScope()) ||
          partialMatchKey(queryKey, queryKeys.unifiedConversationsScope()))
      ) {
        // A conversation list fetched meanwhile (ADR 0077): the conversations
        // still hold the messages on the server, so they come out again here.
        const unified = partialMatchKey(queryKey, queryKeys.unifiedConversationsScope());
        const data = event.query.state.data as ConversationPageCache | undefined;
        const next = removeMessagesFromConversations(data, patch.messageIds, unified ? patch.accountId : undefined);
        if (next !== data) client.setQueryData(queryKey, next);
      } else if (patch.kind === 'messages-moved' || patch.kind === 'messages-removed') {
        // The merged list (ADR 0056) is guarded too, and matched on the
        // account: its rows come from several mailboxes and a message id names
        // no account.
        const unified = partialMatchKey(queryKey, queryKeys.unifiedMessagesScope());
        if (!unified && !partialMatchKey(queryKey, keys.messagesScope())) continue;
        const data = event.query.state.data as MessagePageCache | undefined;
        const next = removeMessageFromList(data, ids, unified ? patch.accountId : undefined);
        if (next !== data) client.setQueryData(queryKey, next);
      }
    }
  });
  let stopped = false;
  return () => {
    if (stopped) return;
    stopped = true;
    for (const entry of held) pendingRemovalSet.delete(entry);
    unsubscribe();
  };
}

/** Messages a pending (undoable, not yet sent) action has taken out of the lists. */
export interface PendingRemoval {
  accountId: string;
  messageIds: readonly string[];
}

const pendingRemovalSet = new Set<PendingRemoval>();

/**
 * What the pending actions are holding out of the lists, for writers that are
 * not fetches and so are not seen by the guard above: a conversation `sync`
 * (ADR 0077) answered before the commit still reports the archived
 * conversation, and must not put it back under the Undo that hides it.
 */
export function pendingRemovals(): readonly PendingRemoval[] {
  return [...pendingRemovalSet];
}

/**
 * Undo, and the rollback of a deferred commit: the removed rows go back where
 * they were, the counters move back by the same deltas, and nothing else in
 * the cache is touched.
 */
function reinstatePatches(
  client: QueryClient,
  snapshot: CacheSnapshot,
  patches: readonly CachePatch[],
): void {
  const ids = removedIds(patches);
  if (ids.size > 0) {
    for (const [queryKey, before] of snapshot) {
      if (isConversationPageCache(before)) {
        const unified = partialMatchKey(queryKey, queryKeys.unifiedConversationsScope());
        for (const patch of patches) {
          if (patch.kind !== 'messages-moved' && patch.kind !== 'messages-removed') continue;
          if (!unified && queryKey[1] !== patch.accountId) continue;
          client.setQueryData<ConversationPageCache>(queryKey, (now) =>
            reinsertConversations(now, before, new Set(patch.messageIds), unified ? patch.accountId : undefined),
          );
        }
      } else if (isPageCache(before)) {
        client.setQueryData<MessagePageCache>(queryKey, (now) => reinsertRows(now, before, ids));
      } else if (isDetailOf(before, ids)) {
        // Delete dropped the detail; move re-homed it. Either way it is back
        // in the folder it was read from.
        client.setQueryData<MessageDetail>(queryKey, (now) =>
          now === undefined ? before : { ...now, folderId: before.folderId },
        );
      }
    }
  }
  for (const patch of patches) {
    if (patch.kind !== 'folder-counts') continue;
    client.setQueryData<Folder[]>(queryKeys.account(patch.accountId).folders(), (folders) =>
      adjustUnreadCount(folders, negate(patch.deltas)),
    );
  }
}

function isPageCache(value: unknown): value is MessagePageCache {
  return typeof value === 'object' && value !== null && Array.isArray((value as MessagePageCache).pages);
}

function isDetailOf(value: unknown, ids: ReadonlySet<string>): value is MessageDetail {
  if (typeof value !== 'object' || value === null) return false;
  const id = (value as { id?: unknown }).id;
  return typeof id === 'string' && ids.has(id);
}

/**
 * Puts each removed row back on the page and at the index it had, in the
 * list's *current* state. A list dropped from the cache in the meantime stays
 * dropped — its next fetch is the truth.
 */
function reinsertRows(
  now: MessagePageCache | undefined,
  before: MessagePageCache,
  ids: ReadonlySet<string>,
): MessagePageCache | undefined {
  if (!now || now.pages.length === 0) return now;
  const present = new Set(now.pages.flatMap((page) => page.items.map((message) => message.id)));
  let pages = now.pages;

  before.pages.forEach((page, pageIndex) => {
    page.items.forEach((message, itemIndex) => {
      if (!ids.has(message.id) || present.has(message.id)) return;
      if (pages === now.pages) pages = [...now.pages];
      const target = Math.min(pageIndex, pages.length - 1);
      const into = pages[target]!;
      const items = [...into.items];
      // Ascending order, so rows removed side by side land side by side.
      items.splice(Math.min(itemIndex, items.length), 0, message);
      pages[target] = {
        ...into,
        items,
        ...(into.total !== undefined ? { total: into.total + 1 } : {}),
      };
      present.add(message.id);
    });
  });

  return pages === now.pages ? now : { ...now, pages };
}

function negate(deltas: FolderCountDeltas): FolderCountDeltas {
  return Object.fromEntries(
    Object.entries(deltas).map(([folderId, delta]) => [
      folderId,
      { unread: -(delta.unread ?? 0), total: -(delta.total ?? 0) },
    ]),
  );
}
